import { MonitoringDataType, MonitoringDataTypeMetadata, MonitoringDataValue, SubscriberProfile } from 'store';

export function ensureId<T>(id: string | undefined, func: (csid: string) => Promise<T>) {
  if (!id) {
    // eslint-disable-next-line i18next/no-literal-string
    throw new Error('Id should not be undefined');
  } else {
    return func(id);
  }
}

export const getMonitoringDataCount = (subscriberProfile: SubscriberProfile, dataType: MonitoringDataType): number =>
  MonitoringDataTypeMetadata[dataType].getValues(subscriberProfile)?.values.length ?? 0;

// eslint-disable-next-line consistent-return
export const isValueEmpty = (value: MonitoringDataValue) => {
  // eslint-disable-next-line default-case
  switch (value.dataType) {
    case 'DriverLicense':
      return !value.value.driverLicenseNumber && !value.value.driverLicenseState;
    case 'Passport':
      return !value.value.passportNumber;
    case 'NationalId':
      return !value.value.id && !value.value.nationalCountryCode;
    case 'BankAccount':
      return !value.value.routingNumber && !value.value.accountNumber;
    case 'EmailAddress':
      return !value.value.email;
    case 'TelephoneNumber':
      return !value.value.telephone;
    case 'CreditCard':
    case 'DebitCard':
      return !value.value.cardNumber;
    case 'MedicalID':
    case 'Iban':
    case 'OtherCard':
    case 'SSN':
      return !value.value;
  }
};

/**
 * Extract the individual fields in a {@link MonitoringDataValue} into an array of strings
 * (e.g. ['123456789', 'CA'] for a driver license number and state, ['1234567890'] for a passport number, etc.)
 *
 * @param monitoringDataValue
 */
// eslint-disable-next-line consistent-return
function extractMonitoringDataValueStrings(monitoringDataValue: MonitoringDataValue): string[] {
  const { dataType, value } = monitoringDataValue;
  // eslint-disable-next-line default-case
  switch (dataType) {
    case 'DriverLicense':
      return [value.driverLicenseNumber, value.driverLicenseState];
    case 'Passport':
      return [value.passportNumber];
    case 'NationalId':
      return [value.id, value.nationalCountryCode];
    case 'BankAccount':
      return [value.routingNumber, value.accountNumber];
    case 'EmailAddress':
      return [value.email];
    case 'TelephoneNumber':
      return [value.telephone];
    case 'CreditCard':
    case 'DebitCard':
      return [value.cardNumber];
    case 'MedicalID':
    case 'Iban':
    case 'OtherCard':
      return [value];
    case 'SSN':
      throw new Error('SSN should not be used in this context'); // since we should never be able to compare SSN value
  }
}

/**
 * Given a user input of a {@link MonitoringDataValue}, find potential duplicates in the subscriber profile cyber data (obfuscated)
 *
 * @param subscriberProfile
 * @param userInput
 */
export function findPotentialDuplicates<T extends MonitoringDataType>(
  subscriberProfile: SubscriberProfile,
  userInput: MonitoringDataValue<T>
): MonitoringDataValue<T>[] {
  const { dataType } = userInput;
  const userInputFields = extractMonitoringDataValueStrings(userInput as MonitoringDataValue);

  return (
    MonitoringDataTypeMetadata[dataType]
      .getValues(subscriberProfile)
      ?.values.map((value) => ({
        // map the cyber data values to MonitoringDataValue<T>
        dataType,
        value,
      }))
      .filter((cyberDataValue) => {
        const obfuscatedFields = extractMonitoringDataValueStrings(cyberDataValue as MonitoringDataValue);
        return (
          obfuscatedFields.length === userInputFields.length &&
          // consider an obfuscated info a potential duplicate if all user input fields end with the corresponding obfuscated field
          obfuscatedFields.every((obfuscatedField, i) => {
            const suffix = obfuscatedField.replace(/^\*+/, ''); // strip leading asterisks
            const userInputField = userInputFields[i]; // corresponding user input field (e.g. driver license number in a driver license)
            return userInputField.endsWith(suffix);
          })
        );
      }) ?? []
  );
}
